import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Button,
    Chip,
    Stack,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    useTheme,
    alpha,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Fullscreen as FullscreenIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { supabase } from '../../../../../supabaseClient';

export default function DeviationReview({ batchLogId, onCapaCreated, onIssueResolved, showSnackbar, canCloseIssues = false, canStartCAPA = false, batchTimeFormat = '12hour' }) {
    const theme = useTheme();
    console.log('DeviationReview batchTimeFormat:', batchTimeFormat);
    const [deviations, setDeviations] = useState([]);
    const [closedDeviations, setClosedDeviations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [historyPage, setHistoryPage] = useState(1);

    const severityConfig = {
        'low': { color: '#4caf50', label: 'Low' },
        'medium': { color: '#ff9800', label: 'Medium' },
        'high': { color: '#f44336', label: 'High' },
        'critical': { color: '#d32f2f', label: 'Critical' }
    };

    const statusConfig = {
        'open': { color: '#ff9800', label: 'Pending Review' },
        'investigating': { color: '#2196f3', label: 'Under Investigation' },
        'capa_required': { color: '#f44336', label: 'CAPA Required' },
        'closed_no_capa': { color: '#4caf50', label: 'Closed - No CAPA' },
        'closed_with_capa': { color: '#4caf50', label: 'Closed - With CAPA' }
    };

    useEffect(() => {
        loadDeviations();
    }, []);

    const loadDeviations = async () => {
        if (!batchLogId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            // Load open deviations for this batch
            const { data: openData, error: openError } = await supabase
                .from('batch_log_deviations')
                .select('*')
                .eq('batch_log_id', batchLogId)
                .eq('status', 'open')
                .order('created_at', { ascending: false });

            if (openError) {
                console.error('Error loading open deviations:', openError);
                showSnackbar?.('Error loading deviations: ' + openError.message, 'error');
                return;
            }

            // Load closed deviations for this batch
            const { data: closedData, error: closedError } = await supabase
                .from('batch_log_deviations')
                .select('*')
                .eq('batch_log_id', batchLogId)
                .in('status', ['closed_no_capa', 'closed_with_capa'])
                .order('resolved_at', { ascending: false })
                .limit(10);

            if (closedError) {
                console.error('Error loading closed deviations:', closedError);
                showSnackbar?.('Error loading closed deviations: ' + closedError.message, 'error');
            }

            setDeviations(openData || []);
            setClosedDeviations(closedData || []);
        } catch (error) {
            console.error('Error loading deviations:', error);
            showSnackbar?.('Error loading deviations: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCapaDecision = async (deviation, requiresCapa) => {
        try {
            const updateData = {
                reviewed_by: 'QA Team',
                resolved_at: new Date().toISOString()
            };

            if (requiresCapa) {
                updateData.status = 'capa_required';
                updateData.requires_capa = true;
                updateData.capa_required = true;
            } else {
                updateData.status = 'closed_no_capa';
                updateData.requires_capa = false;
                updateData.capa_required = false;
            }

            const { error } = await supabase
                .from('batch_log_deviations')
                .update(updateData)
                .eq('id', deviation.id);

            if (error) {
                console.error('Error updating deviation:', error);
                showSnackbar?.('Error updating deviation: ' + error.message, 'error');
                return;
            }

            if (requiresCapa) {
                showSnackbar?.('Investigation started for this issue', 'info');
                onCapaCreated?.();
            } else {
                showSnackbar?.('Issue closed successfully', 'success');
            }
            
            loadDeviations();
            onIssueResolved?.(); // Notify parent that an issue was resolved
        } catch (error) {
            console.error('Error processing CAPA decision:', error);
            showSnackbar?.('Error processing decision: ' + error.message, 'error');
        }
    };

    const getSeverityColor = (severity) => {
        return severityConfig[severity]?.color || '#9e9e9e';
    };

    const getStatusColor = (status) => {
        return statusConfig[status]?.color || '#9e9e9e';
    };

    if (loading) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography>Loading deviation records...</Typography>
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ p: 2 }}>
                <Grid container spacing={3}>
                    {/* Header - Compressed */}
                    <Grid item xs={12}>
                        <Card sx={{ 
                            borderRadius: 4, 
                            border: `1px solid ${theme.palette.grey[400]}`,
                            background: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{
                                        p: 1.5, 
                                        borderRadius: 2, 
                                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                        backdropFilter: 'blur(10px)',
                                        mr: 2
                                    }}>
                                        <Typography variant="h6" sx={{ fontSize: 24, color: theme.palette.primary.main, fontWeight: 500 }}>QR</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                            Quality Review
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Review reported issues and decide if they need formal investigation
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Summary Stats - Compressed Grid */}
                                <Grid container spacing={1}>
                                    <Grid item xs={12} md={4}>
                                        <Card sx={{ 
                                            borderRadius: 4,
                                            border: `1px solid ${theme.palette.grey[400]}`,
                                            background: 'white',
                                            backdropFilter: 'blur(20px)',
                                            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                            height: 120,
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                            }
                                        }}>
                                            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                                <Typography variant="h4" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                                                    {deviations.length}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                                    Total Issues
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Card sx={{ 
                                            borderRadius: 4,
                                            border: `1px solid ${theme.palette.grey[400]}`,
                                            background: deviations.filter(d => d.status === 'open').length > 0 ? alpha(theme.palette.warning.main, 0.05) : alpha(theme.palette.grey[100], 0.5),
                                            backdropFilter: 'blur(20px)',
                                            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                            height: 120,
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                            }
                                        }}>
                                            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                                <Typography variant="h4" sx={{ 
                                                    fontWeight: 500, 
                                                    color: deviations.filter(d => d.status === 'open').length > 0 ? theme.palette.warning.main : theme.palette.text.disabled, 
                                                    mb: 0.5 
                                                }}>
                                                    {deviations.filter(d => d.status === 'open').length}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                                    Pending Review
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Card sx={{ 
                                            borderRadius: 4,
                                            border: `1px solid ${theme.palette.grey[400]}`,
                                            background: deviations.filter(d => d.issue_severity === 'critical').length > 0 ? alpha(theme.palette.error.main, 0.05) : alpha(theme.palette.success.main, 0.05),
                                            backdropFilter: 'blur(20px)',
                                            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                            height: 120,
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                            }
                                        }}>
                                            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                                <Typography variant="h4" sx={{ 
                                                    fontWeight: 500, 
                                                    color: deviations.filter(d => d.issue_severity === 'critical').length > 0 ? theme.palette.error.main : theme.palette.success.main, 
                                                    mb: 0.5 
                                                }}>
                                                    {deviations.filter(d => d.issue_severity === 'critical').length}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                                    Critical Issues
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Deviation Records - Better positioned */}
                    <Grid item xs={12}>
                        {deviations.length === 0 ? (
                            <Card sx={{ 
                                borderRadius: 4, 
                                border: `1px solid ${theme.palette.grey[400]}`,
                                background: 'white',
                                backdropFilter: 'blur(20px)',
                                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                textAlign: 'center',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                                }
                            }}>
                                <CardContent sx={{ p: 4 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.success.main, mb: 1 }}>
                                        No Issues to Review
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                        All reported issues have been reviewed
                                    </Typography>
                                </CardContent>
                            </Card>
                        ) : (
                            <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                <Stack spacing={2}>
                                    {deviations.map((deviation) => {
                                        const severityColor = getSeverityColor(deviation.issue_severity);
                                        const statusColor = getStatusColor(deviation.status);

                                        return (
                                            <Accordion 
                                                key={deviation.id}
                                                sx={{ 
                                                    borderRadius: '12px !important',
                                                    border: `1px solid ${theme.palette.grey[400]}`,
                                                    background: 'white',
                                                    backdropFilter: 'blur(20px)',
                                                    boxShadow: `0 2px 8px ${alpha('#000', 0.08)}, 0 1px 4px ${alpha('#000', 0.04)}`,
                                                    mb: 3,
                                                    mt: 2,
                                                    overflow: 'hidden !important',
                                                    '&:before': { display: 'none' },
                                                    '&:after': { display: 'none' },
                                                    '&.MuiAccordion-root': {
                                                        margin: '8px 0 24px 0 !important',
                                                        boxShadow: `0 2px 8px ${alpha('#000', 0.08)}, 0 1px 4px ${alpha('#000', 0.04)} !important`,
                                                        borderRadius: '12px !important'
                                                    },
                                                    '&.Mui-expanded': {
                                                        margin: '8px 0 24px 0 !important',
                                                        borderRadius: '12px !important',
                                                        boxShadow: `0 4px 16px ${alpha('#000', 0.12)}, 0 2px 8px ${alpha('#000', 0.06)}`
                                                    },
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: `0 8px 24px ${alpha('#000', 0.15)}, 0 4px 12px ${alpha('#000', 0.08)}`,
                                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                                        zIndex: 1
                                                    },
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    '& .MuiAccordionSummary-root': {
                                                        borderRadius: '12px 12px 0 0 !important',
                                                        overflow: 'hidden'
                                                    },
                                                    '& .MuiAccordionDetails-root': {
                                                        borderRadius: '0 0 12px 12px !important',
                                                        overflow: 'hidden'
                                                    }
                                                }}
                                            >
                                                <AccordionSummary 
                                                    expandIcon={<ExpandMoreIcon />}
                                                    sx={{ 
                                                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                                        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                                        borderRadius: '12px 12px 0 0',
                                                        p: 3
                                                    }}
                                                >
                                                                                                         <Grid container alignItems="center" spacing={1}>
                                                        <Grid item xs={12} md={6}>
                                                            <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                                                {((deviation.issue_type?.replace(/_/g, ' ') || 'Unknown')
                                                                    .split(' ')
                                                                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                                                    .join(' '))} Issue
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                                                Reported {dayjs(deviation.created_at).format(batchTimeFormat === '24hour' ? 'MMM DD, YYYY' : 'MMM DD, YYYY')}
                                                            </Typography>
                                                        </Grid>
                                                                                                                 <Grid item xs={12} md={6}>
                                                             <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
                                                                 <Chip
                                                                     label={severityConfig[deviation.issue_severity]?.label || deviation.issue_severity}
                                                                     sx={{
                                                                         backgroundColor: severityColor,
                                                                         color: 'white',
                                                                         fontWeight: 500,
                                                                         borderRadius: 1
                                                                     }}
                                                                 />
                                                                 <Chip
                                                                     label={statusConfig[deviation.status]?.label || deviation.status}
                                                                     sx={{
                                                                         backgroundColor: statusColor,
                                                                         color: 'white',
                                                                         fontWeight: 500,
                                                                         borderRadius: 1
                                                                     }}
                                                                 />
                                                             </Box>
                                                         </Grid>
                                                    </Grid>
                                                </AccordionSummary>

                                                <AccordionDetails sx={{ p: 3 }}>
                                                    <Grid container spacing={3}>
                                                        {/* Issue Details */}
                                                        <Grid item xs={12}>
                                                            <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, color: theme.palette.text.primary }}>
                                                                Issue Description
                                                            </Typography>
                                                            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500, color: theme.palette.text.secondary }}>
                                                                {deviation.issue_description}
                                                            </Typography>
                                                        </Grid>

                                                        {/* Context Information */}
                                                        <Grid item xs={12} md={6}>
                                                            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1, color: theme.palette.text.primary }}>
                                                                Context Information
                                                            </Typography>
                                                            <Stack spacing={1}>
                                                                <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                                                    <strong>Equipment:</strong> {deviation.equipment_used || 'Not specified'}
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                                                    <strong>Shift:</strong> {dayjs(deviation.shift).format(batchTimeFormat === '24hour' ? 'MMM DD, YYYY HH:mm' : 'MMM DD, YYYY h:mm A')}
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                                                    <strong>Reported by:</strong> {deviation.flagged_by_role}
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                                                    <strong>Time:</strong> {dayjs(deviation.flagged_at).format(batchTimeFormat === '24hour' ? 'MMM DD, YYYY HH:mm' : 'MMM DD, YYYY h:mm A')}
                                                                </Typography>
                                                            </Stack>
                                                        </Grid>

                                                        {/* Temperature Data if applicable */}
                                                        {deviation.issue_type === 'temperature_deviation' && deviation.temperature_target && (
                                                            <Grid item xs={12} md={6}>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1, color: theme.palette.text.primary }}>
                                                                    Temperature Data
                                                                </Typography>
                                                                <Stack spacing={1}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                                                        <strong>Target:</strong> {deviation.temperature_target}°C
                                                                    </Typography>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                                                        <strong>Actual:</strong> {deviation.temperature_actual}°C
                                                                    </Typography>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                                                        <strong>Deviation:</strong> {Math.abs(deviation.temperature_actual - deviation.temperature_target)}°C
                                                                    </Typography>
                                                                </Stack>
                                                            </Grid>
                                                        )}

                                                        {/* CAPA Assessment */}
                                                        <Grid item xs={12}>
                                                            <Divider sx={{ my: 2, borderColor: alpha(theme.palette.primary.main, 0.2) }} />
                                                            <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                                                                Quality Assessment
                                                            </Typography>
                                                            
                                                            <Alert 
                                                                severity={deviation.issue_severity === 'critical' ? 'error' : 'warning'}
                                                                sx={{ 
                                                                    mb: 2, 
                                                                    borderRadius: 3,
                                                                    border: `1px solid ${deviation.issue_severity === 'critical' ? theme.palette.error.main : theme.palette.warning.main}`,
                                                                    backgroundColor: alpha(deviation.issue_severity === 'critical' ? theme.palette.error.main : theme.palette.warning.main, 0.1),
                                                                    backdropFilter: 'blur(10px)'
                                                                }}
                                                            >
                                                                <Typography variant="body2" sx={{ fontWeight: 500, color: 'black'}}>
                                                                    <strong>Decision Required:</strong> Does this issue need a formal investigation (CAPA)?
                                                                    {deviation.issue_severity === 'critical' && (
                                                                        <span> Critical issues typically require investigation.</span>
                                                                    )}
                                                                </Typography>
                                                            </Alert>

                                                            {deviation.capa_justification && (
                                                                <Card sx={{ 
                                                                    p: 3, 
                                                                    background: alpha(theme.palette.warning.main, 0.05), 
                                                                    mb: 2, 
                                                                    borderRadius: 3, 
                                                                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                                                                    backdropFilter: 'blur(10px)'
                                                                }}>
                                                                    <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1, color: theme.palette.text.primary }}>
                                                                        Reporter's Note:
                                                                    </Typography>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                                                        {deviation.capa_justification}
                                                                    </Typography>
                                                                </Card>
                                                            )}

                                                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                                                {canCloseIssues && (
                                                                    <Button
                                                                        variant="outlined"
                                                                        onClick={() => handleCapaDecision(deviation, false)}
                                                                        disabled={deviation.status !== 'open'}
                                                                        sx={{
                                                                            borderRadius: 3,
                                                                            px: 4,
                                                                            py: 1.5,
                                                                            borderColor: theme.palette.success.main,
                                                                            color: theme.palette.success.main,
                                                                            fontWeight: 500,
                                                                            background: 'rgba(255, 255, 255, 0.8)',
                                                                            backdropFilter: 'blur(10px)',
                                                                            boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.25)}`,
                                                                            '&:hover': {
                                                                                borderColor: theme.palette.success.main,
                                                                                backgroundColor: alpha(theme.palette.success.main, 0.04),
                                                                                transform: 'translateY(-1px)',
                                                                                boxShadow: `0 6px 20px ${alpha(theme.palette.success.main, 0.3)}`
                                                                            },
                                                                            '&:disabled': {
                                                                                borderColor: theme.palette.grey[300],
                                                                                color: theme.palette.text.disabled,
                                                                                background: alpha(theme.palette.grey[100], 0.5)
                                                                            },
                                                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                                                        }}
                                                                    >
                                                                        Close Issue
                                                                    </Button>
                                                                )}
                                                                {canStartCAPA && (
                                                                    <Button
                                                                        variant="contained"
                                                                        onClick={() => handleCapaDecision(deviation, true)}
                                                                        disabled={deviation.status !== 'open'}
                                                                        sx={{
                                                                            borderRadius: 3,
                                                                            px: 4,
                                                                            py: 1.5,
                                                                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                                                            color: 'white',
                                                                            fontWeight: 500,
                                                                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                                                                            '&:hover': {
                                                                                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                                                                transform: 'translateY(-1px)',
                                                                                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                                                                            },
                                                                            '&:disabled': {
                                                                                background: theme.palette.grey[400],
                                                                                color: theme.palette.grey[100],
                                                                                boxShadow: 'none'
                                                                            },
                                                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                                                        }}
                                                                    >
                                                                        Start Investigation
                                                                    </Button>
                                                                )}
                                                                {!canCloseIssues && !canStartCAPA && (
                                                                    <Typography variant="body2" sx={{ 
                                                                        fontWeight: 500, 
                                                                        color: theme.palette.text.disabled,
                                                                        textAlign: 'center',
                                                                        py: 2
                                                                    }}>
                                                                        No permissions to close issues or start investigations
                                                                    </Typography>
                                                                )}
                                                            </Box>

                                                            {deviation.requires_capa && (
                                                                <Alert 
                                                                    severity="info" 
                                                                    sx={{ 
                                                                        mt: 2, 
                                                                        borderRadius: 3,
                                                                        border: `1px solid ${theme.palette.info.main}`,
                                                                        backgroundColor: alpha(theme.palette.info.main, 0.1),
                                                                        backdropFilter: 'blur(10px)'
                                                                    }}
                                                                >
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                        This issue has been marked for investigation. 
                                                                        A CAPA record will be created automatically.
                                                                    </Typography>
                                                                </Alert>
                                                            )}
                                                        </Grid>
                                                    </Grid>
                                                </AccordionDetails>
                                            </Accordion>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        )}
                    </Grid>

                    {/* Recent Issues History */}
                    {closedDeviations.length > 0 && (
                        <Grid item xs={12}>
                            <Card sx={{ 
                                borderRadius: 4, 
                                border: `1px solid ${theme.palette.grey[400]}`, 
                                backgroundColor: 'white',
                                backdropFilter: 'blur(20px)',
                                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                                },
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.success.main }}>
                                            Recent Issues History ({closedDeviations.length})
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{ 
                                        borderRadius: 3, 
                                        border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                                        background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)}, ${alpha(theme.palette.success.main, 0.02)})`,
                                        boxShadow: `0 12px 32px ${alpha(theme.palette.success.main, 0.15)}`,
                                        overflow: 'hidden',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: `0 20px 40px ${alpha(theme.palette.success.main, 0.25)}`,
                                            border: `1px solid ${alpha(theme.palette.success.main, 0.5)}`
                                        }
                                    }}>
                                        <TableContainer sx={{ overflow: 'visible' }}>
                                            <Table stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ 
                                                            fontWeight: 500,
                                                            fontSize: '0.9rem',
                                                            bgcolor: 'white',
                                                            borderBottom: `2px solid ${theme.palette.success.main}`,
                                                            color: 'success.main',
                                                            py: 2,
                                                            px: 3
                                                        }}>
                                                            Issue Type
                                                        </TableCell>
                                                        <TableCell sx={{ 
                                                            fontWeight: 500,
                                                            fontSize: '0.9rem',
                                                            bgcolor: 'white',
                                                            borderBottom: `2px solid ${theme.palette.success.main}`,
                                                            color: 'success.main',
                                                            py: 2,
                                                            px: 3
                                                        }}>
                                                            Status
                                                        </TableCell>
                                                        <TableCell sx={{ 
                                                            fontWeight: 500,
                                                            fontSize: '0.9rem',
                                                            bgcolor: 'white',
                                                            borderBottom: `2px solid ${theme.palette.success.main}`,
                                                            color: 'success.main',
                                                            py: 2,
                                                            px: 3
                                                        }}>
                                                            Severity
                                                        </TableCell>
                                                        <TableCell sx={{ 
                                                            fontWeight: 500,
                                                            fontSize: '0.9rem',
                                                            bgcolor: 'white',
                                                            borderBottom: `2px solid ${theme.palette.success.main}`,
                                                            color: 'success.main',
                                                            py: 2,
                                                            px: 3
                                                        }}>
                                                            Closed Date
                                                        </TableCell>
                                                        <TableCell sx={{ 
                                                            fontWeight: 500,
                                                            fontSize: '0.9rem',
                                                            bgcolor: 'white',
                                                            borderBottom: `2px solid ${theme.palette.success.main}`,
                                                            color: 'success.main',
                                                            py: 2,
                                                            px: 3
                                                        }}>
                                                            Reviewed By
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {closedDeviations
                                                        .slice((historyPage - 1) * 5, historyPage * 5)
                                                        .map((deviation) => {
                                                            const getSeverityColor = (severity) => {
                                                                switch(severity?.toLowerCase()) {
                                                                    case 'critical': return '#d32f2f';
                                                                    case 'high': return '#f44336';
                                                                    case 'medium': return '#ff9800';
                                                                    case 'low': return '#4caf50';
                                                                    default: return '#9e9e9e';
                                                                }
                                                            };
                                                            
                                                            return (
                                                                <TableRow 
                                                                    key={deviation.id}
                                                                    hover
                                                                    sx={{ 
                                                                        cursor: 'default',
                                                                        transition: 'all 0.2s ease',
                                                                        backgroundColor: 'white',
                                                                        '&:hover': { 
                                                                            bgcolor: alpha(theme.palette.success.main, 0.05),
                                                                            transform: 'translateX(4px)',
                                                                            transition: 'all 0.2s ease',
                                                                            boxShadow: `inset 4px 0 0 ${theme.palette.success.main}`
                                                                        },
                                                                        '& .MuiTableCell-root': {
                                                                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                                                            py: 2.5,
                                                                            px: 3
                                                                        }
                                                                    }}
                                                                >
                                                                    <TableCell>
                                                                        <Typography 
                                                                            variant="body1" 
                                                                            sx={{ 
                                                                                fontWeight: 500,
                                                                                color: 'text.primary',
                                                                                fontSize: '0.95rem'
                                                                            }}
                                                                        >
                                                                            {((deviation.issue_type?.replace(/_/g, ' ') || 'Unknown Issue')
                                                                                .split(' ')
                                                                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                                                                .join(' '))}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Chip
                                                                            label={deviation.status === 'closed_no_capa' ? 'No Investigation' : 'With Investigation'}
                                                                            size="small"
                                                                            sx={{
                                                                                background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                                                                                color: 'white',
                                                                                fontWeight: 500,
                                                                                fontSize: '0.75rem',
                                                                                borderRadius: 2
                                                                            }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Chip 
                                                                            label={(deviation.issue_severity || 'Medium')
                                                                                .split(' ')
                                                                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                                                                .join(' ')}
                                                                            size="small"
                                                                            sx={{ 
                                                                                backgroundColor: getSeverityColor(deviation.issue_severity),
                                                                                color: 'white',
                                                                                fontWeight: 500,
                                                                                fontSize: '0.75rem',
                                                                                borderRadius: 2
                                                                            }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography 
                                                                            variant="body2" 
                                                                            sx={{ 
                                                                                fontWeight: 500,
                                                                                color: 'text.primary',
                                                                                fontSize: '0.9rem'
                                                                            }}
                                                                        >
                                                                                                                                         {deviation.resolved_at ? 
                                                                 dayjs(deviation.resolved_at).format(batchTimeFormat === '24hour' ? 'MMM DD, YYYY HH:mm' : 'MMM DD, YYYY h:mm A') : 
                                                                 (deviation.status === 'closed_with_capa' && deviation.investigation_completed_at) ?
                                                                 dayjs(deviation.investigation_completed_at).format(batchTimeFormat === '24hour' ? 'MMM DD, YYYY HH:mm' : 'MMM DD, YYYY h:mm A') : 'Pending Investigation'
                                                             }
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography 
                                                                            variant="body2" 
                                                                            sx={{ 
                                                                                fontWeight: 500,
                                                                                color: 'text.primary',
                                                                                fontSize: '0.9rem'
                                                                            }}
                                                                        >
                                                                            {deviation.reviewed_by || 'QA Team'}
                                                                        </Typography>
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        

                                    </Box>
                                    
                                    {/* Pagination outside the table container */}
                                    {closedDeviations.length > 5 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3, mb: 1 }}>
                                            {Array.from({ length: Math.ceil(closedDeviations.length / 5) }, (_, index) => (
                                                <Button
                                                    key={index + 1}
                                                    variant={historyPage === index + 1 ? "contained" : "outlined"}
                                                    size="small"
                                                    onClick={() => setHistoryPage(index + 1)}
                                                    sx={{
                                                        minWidth: 36,
                                                        width: 36,
                                                        height: 36,
                                                        p: 0,
                                                        borderRadius: 2,
                                                        borderColor: theme.palette.success.main,
                                                        color: historyPage === index + 1 ? 'white' : theme.palette.success.main,
                                                        background: historyPage === index + 1 
                                                            ? `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
                                                            : 'rgba(255, 255, 255, 0.8)',
                                                        backdropFilter: 'blur(10px)',
                                                        fontWeight: 500,
                                                        fontSize: '0.75rem',
                                                        boxShadow: historyPage === index + 1 
                                                            ? `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`
                                                            : `0 2px 8px ${alpha(theme.palette.success.main, 0.15)}`,
                                                        '&:hover': {
                                                            borderColor: theme.palette.success.main,
                                                            background: historyPage === index + 1 
                                                                ? `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`
                                                                : alpha(theme.palette.success.main, 0.04),
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: `0 6px 16px ${alpha(theme.palette.success.main, 0.25)}`
                                                        },
                                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                                    }}
                                                >
                                                    {index + 1}
                                                </Button>
                                            ))}
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                </Grid>

                <Alert 
                    severity="info" 
                    sx={{ 
                        mt: 3, 
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.info.main}`,
                        backgroundColor: alpha(theme.palette.info.main, 0.1),
                        backdropFilter: 'blur(10px)'
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        <strong>Close Issue:</strong> Minor problem that doesn't need investigation - issue is resolved.<br/>
                        <strong>Start Investigation:</strong> Serious issue that needs root cause analysis and corrective actions.
                    </Typography>
                </Alert>
            </Box>
        </LocalizationProvider>
    );
} 